import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";

const BACKGROUND_VERSION = "20260606-2126";

export default function PageLayout({ children }) {
  const location = useLocation();
  const isHome = location.pathname === "/" || location.pathname === "/es";
  const homePath = location.pathname === "/es" || location.pathname.startsWith("/es/") ? "/es" : "/";
  const navWrapRef = useRef(null);

  // Keep navbar height CSS variable updated
  useEffect(() => {
    const apply = () => {
      const h = navWrapRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty("--navH", `${h}px`);
    };

    apply();

    const ro = new ResizeObserver(apply);
    if (navWrapRef.current) ro.observe(navWrapRef.current);

    window.addEventListener("resize", apply);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, []);

  return (
    <div className="min-h-screen relative font-[Cormorant_Garamond] text-center">

      {/* Fixed background */}
      <div className="fixed-background">

        <div className="bg-fallback" />

        <div className="animated-gradient" />

        <div className="parchment-bg" />

        <div className="blurred-bg" />

        <div className="light-glow" />

      </div>

      {/* Main content */}
      <div className="relative z-10">

        <div id="site-nav" ref={navWrapRef}>
          <Navbar />
        </div>

        {/* Logo */}
        <div className="w-full flex justify-center pt-12 pb-24 sm:pt-14 sm:pb-20 min-[1180px]:hidden">
          <Link to={homePath}>
            <img
              src="/icons/logo.webp"
              alt="TarotFate.com"
              className="w-[240px] sm:w-[265px] h-auto object-contain animate-mysticReveal"
              onContextMenu={(e) => e.preventDefault()}
            />
          </Link>
        </div>

        {/* Page card */}
        <div
          className={
            isHome
              ? "max-w-[1150px] mx-auto px-4 min-[1180px]:mt-16"
              : "w-full max-w-[95%] sm:max-w-2xl lg:max-w-4xl xl:max-w-[1000px] mx-auto min-[1180px]:mt-16 bg-white/15 backdrop-blur-sm border border-[#e6dff5]/50 rounded-lg p-4 sm:p-6 text-left text-[#3f2d61] shadow-md"
          }
        >
          {children}
        </div>

        <br />

      </div>

      {/* Footer */}
      <div className="relative z-8">
        <Footer />
      </div>

      <style>{`

        /* Background container */

        .fixed-background {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          min-height: 100vh;
          z-index: -60;
          pointer-events: none;
          overflow: hidden;

          transform: translateZ(0);
          will-change: transform;
        }

        .fixed-background > div {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;

          transform: translateZ(0);
          will-change: transform, opacity;
        }

        /* Fallback color */

        .bg-fallback {
          background: #0f3d3f;
        }

        /* Animated gradient */

        .animated-gradient {
          background: linear-gradient(135deg, #e9fffb, #c9fbf3, #f4fffd);
          background-size: 400% 400%;
          animation: gradientAnimation 15s ease infinite;

          will-change: background-position;
          transform: translateZ(0);
        }

        /* Main parchment */

        .parchment-bg {
          background-image: url('/icons/background1.webp?v=${BACKGROUND_VERSION}');
          background-repeat: no-repeat;
          background-size: cover;
          background-position: bottom;

          filter: brightness(1.06) saturate(112%);

          transform: translateZ(0);
        }

        /* Soft blurred background */

        .blurred-bg {
          display: none;

          background-image: url('/icons/background1.webp?v=${BACKGROUND_VERSION}');
          background-repeat: no-repeat;
          background-size: 102% 102%;
          background-position: center;

          filter: brightness(1.05) saturate(112%) blur(1.5px);

          transform: translateZ(0);
        }

        /* Glow overlay */

        .light-glow {
          background:
            radial-gradient(70% 55% at 50% 12%, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 10%, rgba(255,255,255,.04) 70%),
            radial-gradient(120% 20% at 45% 10%, rgba(0,70,80,.04) 10%, rgba(0,80,70,.02) 35%, rgba(0,60,60,.08) 80%),
            linear-gradient(135deg, rgba(0,100,90,.12) 10%, rgba(0,110,101,.08) 42%, rgba(0,70,70,.08) 80%);
        }

        /* Gradient animation */

        @keyframes gradientAnimation {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        /* Desktop blur */

        @media (min-width: 768px) {
          .blurred-bg {
            display: block;
          }
        }

        /* Mobile stability */

        @media (max-width: 768px) {
          .fixed-background {
            min-height: 100vh;
            height: -webkit-fill-available;
          }

          .parchment-bg {
            background-attachment: initial;
          }
        }

        /* iOS fix */

        @supports (-webkit-touch-callout: none) {
          .fixed-background {
            min-height: -webkit-fill-available;
          }
        }

      `}</style>
    </div>
  );
}
